import { LoginCallback } from '@okta/okta-react';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFeatureFlagsQuery } from '@/hooks/queries/use-feature-flags';
import { Layout } from '@/Layout';
import { SecureRoute } from '@/router/SecureRouter';

const Login = lazy(() => import('@/components/Login'));
const CapitalGainsWorkflow = lazy(() => import('@/pages/CapitalGainsWorkflow'));
const ClientDetail = lazy(() => import('@/pages/ClientDetail'));
const ClientJobDetail = lazy(() => import('@/pages/ClientJobDetail'));
const Clients = lazy(() => import('@/pages/Clients'));
const Contacts = lazy(() => import('@/pages/Contacts'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const DocumentSplitter = lazy(() => import('@/pages/DocumentSplitter'));
const Extractors = lazy(() => import('@/pages/Extractors'));
const FbarGenerator = lazy(() => import('@/pages/FbarGenerator'));
const PayslipExtractor = lazy(() => import('@/pages/PayslipExtractor'));
const Settings = lazy(() => import('@/pages/Settings'));
const Staff = lazy(() => import('@/pages/Staff'));
const StorageBrowser = lazy(() => import('@/pages/StorageBrowser'));

// Gates the file browser behind the `tax_file_browser` flag. The flags query is
// disabled until an org is selected, so wait on it before deciding to avoid a
// premature redirect.
function StorageRoute() {
  const { selectedOrganizationId } = useOrganization();
  const { data, isPending } = useFeatureFlagsQuery();
  if (!selectedOrganizationId || isPending) {
    return null;
  }
  if (!data?.flags.tax_file_browser) {
    return <Navigate replace to="/dashboard" />;
  }
  return <StorageBrowser />;
}

export function CapitalGainsWorkflowRoute() {
  const { selectedOrganizationId } = useOrganization();
  const { data, isPending } = useFeatureFlagsQuery();
  if (!selectedOrganizationId || isPending) {
    return null;
  }
  if (!data?.flags.tax_cg_workflow) {
    return <Navigate replace to="/dashboard" />;
  }
  return <CapitalGainsWorkflow />;
}

// Gates the org-management surface (Clients & Contacts) behind the
// shared `tax_org_management` flag. Wraps the `organization` route subtree; the
// flags query is disabled until an org is selected, so wait before deciding.
function OrgManagementRoute() {
  const { selectedOrganizationId } = useOrganization();
  const { data, isPending } = useFeatureFlagsQuery();
  if (!selectedOrganizationId || isPending) {
    return null;
  }
  if (!data?.flags.tax_org_management) {
    return <Navigate replace to="/dashboard" />;
  }
  return <Outlet />;
}

export const AppRouter = () => {
  return (
    <Router>
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-gray-900 border-b-2" />
          </div>
        }
      >
        <Routes>
          <Route element={<Login />} path="/login" />
          <Route element={<LoginCallback />} path="/login/callback" />
          <Route
            element={
              <SecureRoute>
                <Layout />
              </SecureRoute>
            }
            path="/"
          >
            <Route element={<Navigate replace to="/dashboard" />} index />
            <Route element={<Dashboard />} path="dashboard" />
            <Route path="extractors">
              <Route element={<Extractors />} index />
              <Route element={<PayslipExtractor />} path="payslip" />
              {/* The v1 extractor is retired; keep old bookmarks working by
                  forwarding them to the v2 workflow. */}
              <Route element={<Navigate replace to="/extractors/capital-gains-v2" />} path="capital-gains" />
              <Route element={<CapitalGainsWorkflowRoute />} path="capital-gains-v2/*" />
              <Route element={<FbarGenerator />} path="fbar" />
              <Route element={<DocumentSplitter />} path="document-splitter" />
            </Route>
            <Route element={<Staff />} path="staff" />
            <Route element={<OrgManagementRoute />} path="organization">
              <Route element={<Clients />} path="clients" />
              <Route element={<ClientDetail />} path="clients/:clientAccountId" />
              <Route element={<ClientJobDetail />} path="clients/:clientAccountId/projects/:jobId" />
              <Route element={<Contacts />} path="contacts" />
              <Route element={<Contacts />} path="contacts/:contactId" />
              <Route element={<Settings />} path="settings" />
            </Route>
            <Route element={<StorageRoute />} path="storage" />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
};
